import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';

import { UserSessionRequest } from './authService';
import { CustomSourceIdField } from './components/CustomSourceId';
import { FeatureToggle } from './components/FeatureToggle';
import { featureFlags as flags } from './featureFlags';
import LogoutButton from './LogoutButton';
import MicroFrontendLoader from './MicroFrontendLoader';
import Profile from './Profile';
import { products, createUserRequestBody } from './userConfig';

export const HostLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>(flags);

  // State for product, user, version, and mount type selection
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<'legacy' | 'flexible'>('legacy');
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);

  const [mountType, setMountType] = useState<'widget' | 'container'>(() =>
    localStorage.getItem('mountToContainer') === 'true' ? 'container' : 'widget'
  );
  const [mountKey, setMountKey] = useState(0);
  const [isMounted, setIsMounted] = useState(true);

  // Update available users when product changes
  useEffect(() => {
    if (selectedProduct) {
      const product = products.find((p) => p.id === selectedProduct);
      if (product) {
        const users = product.users.map((user) => `${user.first_name} ${user.last_name}`);
        setAvailableUsers(users);
        setSelectedUser(''); // Reset selected user when product changes
      }
    } else {
      setAvailableUsers([]);
      setSelectedUser('');
    }
  }, [selectedProduct]);

  // State for storing the request body and product key
  // Using the UserSessionRequest type for the request body
  const [userSessionInfo, setUserSessionInfo] = useState<{
    requestBody?: UserSessionRequest;
    productKey?: string;
  }>({});

  // Update session info when product or user changes
  useEffect(() => {
    if (selectedProduct && selectedUser) {
      const result = createUserRequestBody(selectedProduct, selectedUser);

      if (result) {
        const { requestBody, productKey } = result;
        // Selected user and product information ready for session creation
        setUserSessionInfo({ requestBody, productKey });
        setIsMounted(true);
      } else {
        setUserSessionInfo({});
      }
    } else {
      setUserSessionInfo({});
    }
  }, [selectedProduct, selectedUser]);

  const onChangeCustomId = (id: string) => {
    setUserSessionInfo((prevState) => {
      if (Boolean(id) && prevState?.requestBody?.data?.attributes) {
        return {
          ...prevState,
          requestBody: {
            ...prevState.requestBody,
            data: {
              ...prevState.requestBody.data,
              attributes: {
                ...prevState.requestBody.data.attributes,
                source_account_id: id,
              },
            },
          },
        };
      }
      return prevState;
    });
  };

  const handleFeatureFlagToggle = (key: string, value: boolean) => {
    setFeatureFlags((prevFlags) => ({
      ...prevFlags,
      [key]: value,
    }));
  };

  // Effect to handle mount type changes
  useEffect(() => {
    // Mount type changes handled here

    if (mountType === 'container') {
      localStorage.setItem('mountToContainer', 'true');
      if (location.pathname !== '/fixed') {
        navigate('/fixed', { replace: true });
      }
    } else {
      localStorage.removeItem('mountToContainer');
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
    }

    // Force a re-render of the MicroFrontendLoader when mount type changes
    setUserSessionInfo((prevState) => ({ ...prevState }));
  }, [mountType, navigate, location.pathname]);

  // Effect to handle version changes
  useEffect(() => {
    // Force a re-render of the MicroFrontendLoader when version changes
    setUserSessionInfo((prevState) => ({ ...prevState }));
  }, [selectedVersion]);

  // Removed test functions for token updates

  const handleUnmount = async () => {
    if (selectedVersion === 'legacy') {
      // V1 unmount
      if (window.unmountMicroApp && typeof window.unmountMicroApp === 'function') {
        await window.unmountMicroApp();
      }
    } else {
      // V2 unmount
      if (window.AskAries?.unmount && typeof window.AskAries.unmount === 'function') {
        await window.AskAries.unmount();
      }
    }
    setIsMounted(false);
  };

  const handleRemount = () => {
    // Force remount by changing the key, which will trigger a full reload of MicroFrontendLoader
    setMountKey(prev => prev + 1);
    setIsMounted(true);
  };

  const isWidgetMode = mountType === 'widget';
  const isFixedRoute = location.pathname === '/fixed';

  return (
    <div className='bg-slate-100 flex h-screen w-full flex-col'>
      <div className='bg-slate-700 flex h-16 items-center justify-between px-4' id='unique-id'>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className='text-slate-100 mr-4 sm:hidden'>
          ☰
        </button>
        <div className='text-slate-100 text-2xl'>Ask Aries</div>
        {selectedProduct && selectedUser && (
          isMounted ? (
            <button
              onClick={handleUnmount}
              className="px-4 py-2 ml-4 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 transition-all duration-200 shadow-sm hover:shadow-md">
              Unmount Aries
            </button>
          ) : (
            <button
              onClick={handleRemount}
              className="px-4 py-2 ml-4 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 transition-all duration-200 shadow-sm hover:shadow-md">
              Remount Aries
            </button>
          )
        )}
        {isWidgetMode && <div id='aries-trigger' className='ml-auto mr-8' />}
        <div className='text-slate-10 flex justify-between'>
          <Profile />
          <LogoutButton />
        </div>
      </div>

      <div className='flex flex-1 overflow-hidden'>
        <div
          className={`bg-slate-200 fixed inset-y-0 left-0 z-30 mt-16 flex w-64 transform flex-col transition-transform duration-200 ease-in-out sm:relative sm:mt-0 sm:translate-x-0 sm:transform-none ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
          <div className='flex-1 p-4 overflow-y-auto'>
            <div className='mb-8 space-y-1.5'>
              <button className='text-slate-600 hover:bg-slate-100 hover:text-slate-800 w-full rounded-md px-3 py-1.5 text-left font-medium transition-colors'>
                Menu Item 1
              </button>
              <button className='text-slate-600 hover:bg-slate-100 hover:text-slate-800 w-full rounded-md px-3 py-1.5 text-left font-medium transition-colors'>
                Menu Item 2
              </button>
              <button className='text-slate-600 hover:bg-slate-100 hover:text-slate-800 w-full rounded-md px-3 py-1.5 text-left font-medium transition-colors'>
                Menu Item 3
              </button>
              <button className='text-slate-600 hover:bg-slate-100 hover:text-slate-800 w-full rounded-md px-3 py-1.5 text-left font-medium transition-colors'>
                Menu Item 4
              </button>
            </div>

            <div className='space-y-1.5'>
              <div className='border-slate-300 mb-2 border-t'></div>
              <div className='overflow-x-hidden'>
                {/* Version Selection - shown as the first option - to avoid api calls */}
                <div className='mb-2 mt-4'>
                  <label
                    htmlFor='version-select'
                    className='text-slate-700 mb-1 block text-sm font-medium'>
                    Select Version
                  </label>
                  <select
                    id='version-select'
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value as 'legacy' | 'flexible')}
                    className='border-slate-300 text-slate-700 block w-full rounded-md border bg-white p-2 text-sm'>
                    <option value=''>Select a version</option>
                    <option value='legacy'>Legacy (v1)</option>
                    <option value='flexible'>Flexible (v2)</option>
                  </select>
                </div>
                {/* Product Selection */}
                <div className='mb-4'>
                  <label
                    htmlFor='product-select'
                    className='text-slate-700 mb-1 block text-sm font-medium'>
                    Select Product
                  </label>
                  <select
                    id='product-select'
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className='border-slate-300 text-slate-700 block w-full rounded-md border bg-white p-2 text-sm'>
                    <option value=''>Select a product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* User Selection - only shown when a product is selected */}
                {selectedProduct && (
                  <div className='mb-4'>
                    <label
                      htmlFor='user-select'
                      className='text-slate-700 mb-1 block text-sm font-medium'>
                      Select User
                    </label>
                    <select
                      id='user-select'
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className='border-slate-300 text-slate-700 block w-full rounded-md border bg-white p-2 text-sm'>
                      <option value=''>Select a user</option>
                      {availableUsers.map((user) => (
                        <option key={user} value={user}>
                          {user}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Show selected user information */}
                {selectedProduct && selectedUser && (
                  <>
                    <div className='bg-slate-100 mt-2 rounded-md p-2 text-sm'>
                      <p className='font-medium'>Active User:</p>
                      <p>
                        {selectedUser} ({products.find((p) => p.id === selectedProduct)?.name})
                      </p>
                    </div>

                    <CustomSourceIdField onChangeCustomId={onChangeCustomId} />
                  </>
                )}

                {/* Mount Type Selection - shown as the third option */}
                <div className='mb-2 mt-4'>
                  <label
                    htmlFor='mount-type-select'
                    className='text-slate-700 mb-1 block text-sm font-medium'>
                    Select Mount Type
                  </label>
                  <select
                    id='mount-type-select'
                    value={mountType}
                    onChange={(e) => setMountType(e.target.value as 'widget' | 'container')}
                    className='border-slate-300 text-slate-700 block w-full rounded-md border bg-white p-2 text-sm'>
                    <option value='widget'>Mount as widget</option>
                    <option value='container'>Mount to container</option>
                  </select>
                </div>

                {/* Feature Flags Toggle */}
                <FeatureToggle
                  featureFlags={featureFlags}
                  onToggle={handleFeatureFlagToggle}
                  className='mb-4'
                />

              </div>
            </div>
          </div>

          {/* Removed test buttons section */}
        </div>

        {isSidebarOpen && (
          <div
            className='fixed inset-0 bg-black bg-opacity-50 sm:hidden'
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <div className={`bg-slate-100 flex-1 overflow-auto ${!isFixedRoute ? 'p-6' : ''}`}>
          {/* In container mode with selection, only Outlet is needed for the container */}
          {!isWidgetMode && selectedProduct && selectedUser ? (
            <>
              <Outlet />
              {/* Add the loader component but it won't be visible - it handles the mounting logic */}
              <div className='hidden'>
                <MicroFrontendLoader
                  key={mountKey}
                  requestBody={userSessionInfo.requestBody}
                  productKey={userSessionInfo.productKey}
                  isWidgetMode={isWidgetMode}
                  version={selectedVersion || 'legacy'}
                  featureFlags={featureFlags}
                />
              </div>
            </>
          ) : (
            <>
              {/* In widget mode or when no selection is made */}
              {(!selectedProduct || !selectedUser) && <Outlet />}

              {/* Render the MicroFrontendLoader when product and user are selected in widget mode */}
              {selectedProduct && selectedUser && userSessionInfo.requestBody && (
                <MicroFrontendLoader
                  key={mountKey}
                  requestBody={userSessionInfo.requestBody}
                  productKey={userSessionInfo.productKey}
                  isWidgetMode={isWidgetMode}
                  version={selectedVersion || 'legacy'}
                  featureFlags={featureFlags}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
